import { useEffect, useRef } from "react";

import { Dialog, Icons, LoadingState } from "../../../../../ds";

export interface FileDropHandlers {
    /** A file dragged in from the OS entered the window. */
    onEnter: () => void;
    /** Files were dropped anywhere on the window. */
    onDrop: (files: File[]) => void;
    /** Off while a layer that handles its own files (the editor, the media panel) is open. */
    enabled?: boolean;
}

function hasFiles(ev: DragEvent): boolean {
    return !!ev.dataTransfer?.types && Array.from(ev.dataTransfer.types).indexOf("Files") !== -1;
}

/**
 * Window-wide file drop (the old `DropzoneProvider`): dragging a file in opens "Drop File", dropping it anywhere uploads it.
 * Text dragged inside the page (editing, the tree's pointer drag) is ignored.
 */
export function useFileDrop(handlers: FileDropHandlers) {
    const ref = useRef(handlers);
    ref.current = handlers;
    const enabled = handlers.enabled ?? true;

    useEffect(() => {
        if (!enabled) return;
        const onEnter = (ev: DragEvent) => {
            if (hasFiles(ev)) ref.current.onEnter();
        };
        // without preventDefault on dragover and drop the browser opens the file instead
        const onOver = (ev: DragEvent) => {
            if (hasFiles(ev)) ev.preventDefault();
        };
        const onDrop = (ev: DragEvent) => {
            if (!hasFiles(ev)) return;
            ev.preventDefault();
            const files = ev.dataTransfer?.files ? Array.from(ev.dataTransfer.files) : [];
            if (files.length) ref.current.onDrop(files);
        };
        window.addEventListener("dragenter", onEnter);
        window.addEventListener("dragover", onOver);
        window.addEventListener("drop", onDrop);
        return () => {
            window.removeEventListener("dragenter", onEnter);
            window.removeEventListener("dragover", onOver);
            window.removeEventListener("drop", onDrop);
        };
    }, [enabled]);
}

export interface IDropZonePopupProps {
    open: boolean;
    isUploading?: boolean;
    onClose: () => void;
}

/** "Drop File": the target shown while a file is dragged over the page, then the upload's loader. */
export function DropZonePopup(props: IDropZonePopupProps) {
    return (
        <Dialog open={props.open} onClose={props.onClose} title="Drop File" size="md">
            {props.isUploading ? (
                <LoadingState text="Uploading…" />
            ) : (
                <div className="ark-drop-target">
                    <Icons.upload size={28} />
                    <strong>Drop your files here</strong>
                    <span className="ark-muted">Ark uploads them to this page and copies their markdown.</span>
                </div>
            )}
        </Dialog>
    );
}
